import React, { useState, useEffect } from "react";
import { useContentsList } from "../../hooks/useContentsList";
import "./ContentSelectionModal.css";

interface ContentSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedContentIds: string[]) => void;
  onPreview?: (content: any) => void;
  title?: string;
  existingContentIds?: string[];
  materials?: any[]; // Lista de materiais para filtrar conteúdos já usados
  isAddingToExisting?: boolean; // Se está adicionando a um material existente
  currentMaterialId?: string; // ID do material atual (para permitir seus próprios conteúdos)
}

const ContentSelectionModal: React.FC<ContentSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onPreview,
  title = "Selecionar Conteúdos",
  existingContentIds = [],
  materials = [],
  isAddingToExisting = false,
  currentMaterialId = "",
}) => {
  const { contents, isLoading, error, fetchContents } =
    useContentsList();
  const [selectedContentIds, setSelectedContentIds] =
    useState<string[]>(existingContentIds);
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      // Buscar TODOS os conteúdos (agora a relação é muitos-para-muitos, então um conteúdo pode estar em vários materiais)
      fetchContents();
      setSelectedContentIds(existingContentIds);
    }
  }, [isOpen, existingContentIds, fetchContents]);

  const handleContentToggle = (contentId: string) => {
    setSelectedContentIds((prev) => {
      if (prev.includes(contentId)) {
        return prev.filter((id) => id !== contentId);
      } else {
        return [...prev, contentId];
      }
    });
  };

  const handleConfirm = () => {
    onConfirm(selectedContentIds);
    onClose();
  };

  // Filtrar conteúdos apenas por busca (a API já retorna apenas conteúdos disponíveis)
  const filteredContents = contents.filter((content) => {
    const matchesSearch =
      content.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (content.description &&
        content.description.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="content-selection-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-content">
          <div className="search-section">
            <input
              type="text"
              placeholder="Buscar conteúdos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="contents-list">
            {isLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Carregando conteúdos...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <p>❌ {error}</p>
                <button
                  onClick={fetchContents}
                  className="retry-button"
                >
                  Tentar novamente
                </button>
              </div>
            ) : filteredContents.length === 0 ? (
              <div className="empty-state">
                {searchTerm ? (
                  <p>Nenhum conteúdo encontrado com "{searchTerm}"</p>
                ) : contents.length === 0 ? (
                  <p>📋 Nenhum conteúdo cadastrado ainda</p>
                ) : (
                  <p>📝 Nenhum conteúdo encontrado</p>
                )}
              </div>
            ) : (
              filteredContents.map((content) => (
                <div
                  key={content.id}
                  className={`content-selection-modal-item ${
                    selectedContentIds.includes(content.id)
                      ? "content-selection-modal-item-selected"
                      : ""
                  }`}
                  onClick={() => handleContentToggle(content.id)}
                >
                  <div className="content-selection-modal-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedContentIds.includes(content.id)}
                      onChange={() => handleContentToggle(content.id)}
                      className="content-selection-modal-checkbox-input"
                    />
                  </div>
                  <div className="content-selection-modal-info">
                    <h4 className="content-selection-modal-name">
                      {content.name}
                    </h4>
                    {content.description && (
                      <p className="content-selection-modal-description">
                        {content.description}
                      </p>
                    )}
                    <div className="content-selection-modal-meta">
                      <span className="content-selection-modal-type">
                        {content.type}
                      </span>
                    </div>
                  </div>
                  <div className="content-selection-modal-actions">
                    <button
                      className="content-selection-modal-preview-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onPreview) {
                          onPreview(content);
                        }
                      }}
                      title="Visualizar conteúdo"
                    >
                      👁️
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="modal-footer">
          <div className="selection-info">
            <span>{selectedContentIds.length} conteúdo(s) selecionado(s)</span>
          </div>
          <div className="modal-actions">
            <button className="cancel-button" onClick={onClose}>
              Cancelar
            </button>
            <button
              className="confirm-button"
              onClick={handleConfirm}
              disabled={selectedContentIds.length === 0}
            >
              Confirmar Seleção
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentSelectionModal;
